import { apiFetch } from '@/api/client';
import type { DomainStatus, Site, SiteContext, SiteDetail, SiteStatus } from '@/types';

export interface CreateSiteInput {
  name: string;
  slug: string;
  topic_focus: string;
  default_topics: string[];
  domain?: string;
  site_url?: string;
  theme?: string;
  site_audience?: string;
  regulatory_framing?: string;
  enabled_packs?: string[];
  site_context_json?: string;
  // SeedPrompts is the optional Phase P2 wizard Step 6 payload. Keys are
  // persona names ("writer"|"reviewer"|"brainstorm"|"illustrator"); each
  // entry carries the AI-generated body and the generator template's
  // version ID so the lineage timeline can show the AI badge. Absent or
  // empty falls back to template seeding (Skip path).
  seed_prompts?: Record<string, { body: string; generator_version_id?: string }>;
}

export type GeneratorPersona = 'writer' | 'reviewer' | 'brainstorm' | 'illustrator';

export interface GeneratePromptResult {
  body: string;
  token_count: number;
  warnings: string[];
  generator_version_id: string;
}

export interface GeneratePromptsPreviewRequest {
  site_context_json: string;
  enabled_packs: string[];
  personas: GeneratorPersona[];
  seed_from: 'template';
  feedback?: string;
}

export interface GeneratePromptsForSiteRequest {
  personas: GeneratorPersona[];
  seed_from: 'current' | 'template';
  feedback?: string;
  example_article_ids?: string[];
}

export interface GeneratePromptsResponse {
  generated: Partial<Record<GeneratorPersona, GeneratePromptResult>>;
  errors: Partial<Record<GeneratorPersona, string>>;
}

export interface AcceptedPromptEntry {
  id: string;
  version: number;
}

// AcceptGeneratedPromptsResponse mirrors the backend handler's envelope at
// 200 / 207 / 500. `saved` is omitted when every persona failed; `errors`
// is omitted when every persona saved. The legacy `activated` alias is
// preserved on the response side for transitional callers.
export interface AcceptGeneratedPromptsResponse {
  status: 'ok' | 'partial' | 'failed';
  saved?: Record<string, AcceptedPromptEntry>;
  errors?: Record<string, string>;
}

export interface AcceptGeneratedPromptsResult {
  status: 'ok' | 'partial' | 'failed';
  saved: Record<string, AcceptedPromptEntry>;
  errors: Record<string, string>;
  // activated is a deprecated alias for `saved`; both are populated.
  activated: Record<string, AcceptedPromptEntry>;
}

// AuthoritySourceSuggestion mirrors the AI-generated authority source row
// the wizard's Step 7 and the per-site Suggest modal both render. The five
// trust levels map onto the dashboard's category vocabulary via the
// backend's trust_level → category mapping during bulk-create.
export interface AuthoritySourceSuggestion {
  domain: string;
  name: string;
  description: string;
  url: string;
  trust_level:
    | 'institutional'
    | 'academic'
    | 'industry'
    | 'government'
    | 'journalistic';
}

export interface GenerateAuthoritySourcesPreviewRequest {
  site_context_json: string;
  enabled_packs: string[];
  feedback?: string;
}

export interface GenerateAuthoritySourcesPreviewResponse {
  suggestions: AuthoritySourceSuggestion[];
  warnings: string[];
}

export interface GenerateAuthoritySourcesResponse {
  suggestions: AuthoritySourceSuggestion[];
  existing_domains: string[];
  warnings: string[];
}

export interface BulkAuthoritySourceInput {
  domain: string;
  name: string;
  url?: string;
  category?: string;
  trust_level?: string;
  topics?: string[];
  is_active?: boolean;
}

export interface BulkAuthoritySourcesResponse {
  created: number;
  ids: string[];
}

export interface BootstrapContextResponse {
  context: SiteContext;
  warning?: 'partial_parse';
  raw?: string;
}

export interface RegenerateContextResponse {
  context: SiteContext;
  previous: SiteContext;
  warning?: 'partial_parse';
  raw?: string;
}

export interface UpdateSiteInput {
  name?: string;
  topic_focus?: string;
  site_audience?: string;
  regulatory_framing?: string;
  default_topics?: string[];
  theme?: string;
  gsc_property_url?: string;
  enabled_packs?: string[];
  site_context_json?: string;
}

export interface AddDomainResponse {
  domain: string;
  status: string;
  ssl_status: string;
  cname_target: string;
}

export interface RemoveSiteOptions {
  hard?: boolean;
  confirmSlug?: string;
}

export interface CreateSiteResponse {
  id: string;
  slug: string;
  status: string;
  scaffold_started_at: string;
}

export interface SitesListOptions {
  status?: SiteStatus;
  q?: string;
}

export const sitesApi = {
  list: (opts: SitesListOptions = {}) => {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.q) params.set('q', opts.q);
    const qs = params.toString();
    return apiFetch<Site[]>(qs ? `/sites/?${qs}` : '/sites/');
  },
  get: (id: string) => apiFetch<SiteDetail>(`/sites/${encodeURIComponent(id)}`),
  create: (input: CreateSiteInput) =>
    apiFetch<CreateSiteResponse>('/sites/', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  update: (id: string, input: UpdateSiteInput) =>
    apiFetch<Site>(`/sites/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  remove: (id: string, opts: RemoveSiteOptions = {}) => {
    const qs = opts.hard ? '?hard=true' : '';
    const headers: Record<string, string> | undefined = opts.confirmSlug
      ? { 'X-Confirm-Slug': opts.confirmSlug }
      : undefined;
    return apiFetch<void>(`/sites/${encodeURIComponent(id)}${qs}`, {
      method: 'DELETE',
      headers,
    });
  },
  addDomain: (id: string, domain: string) =>
    apiFetch<AddDomainResponse>(`/sites/${encodeURIComponent(id)}/domains`, {
      method: 'POST',
      body: JSON.stringify({ domain }),
    }),
  removeDomain: (id: string, domain: string) =>
    apiFetch<void>(
      `/sites/${encodeURIComponent(id)}/domains/${encodeURIComponent(domain)}`,
      { method: 'DELETE' },
    ),
  domainStatus: (id: string, domain: string) =>
    apiFetch<DomainStatus>(
      `/sites/${encodeURIComponent(id)}/domains/${encodeURIComponent(domain)}/status`,
    ),
  rescaffold: (id: string) =>
    apiFetch<{ status: string }>(
      `/sites/${encodeURIComponent(id)}/rescaffold`,
      { method: 'POST' },
    ),
  uploadFavicon: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiFetch<{ path: string }>(
      `/sites/${encodeURIComponent(id)}/assets/favicon`,
      { method: 'POST', body: fd },
    );
  },
  uploadOGImage: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return apiFetch<{ path: string }>(
      `/sites/${encodeURIComponent(id)}/assets/og-image`,
      { method: 'POST', body: fd },
    );
  },
  resetFavicon: (id: string) =>
    apiFetch<void>(`/sites/${encodeURIComponent(id)}/assets/favicon`, {
      method: 'DELETE',
    }),
  resetOGImage: (id: string) =>
    apiFetch<void>(`/sites/${encodeURIComponent(id)}/assets/og-image`, {
      method: 'DELETE',
    }),
  reIllustrate: (id: string) =>
    apiFetch<ReIllustrateResponse>(
      `/sites/${encodeURIComponent(id)}/re-illustrate`,
      { method: 'POST' },
    ),
  exportZipUrl: (id: string) => `/api/sites/${encodeURIComponent(id)}/export`,
  packUsage: (id: string, packID: string) =>
    apiFetch<{ count: number }>(
      `/sites/${encodeURIComponent(id)}/pack-usage?pack=${encodeURIComponent(packID)}`,
    ),
  bootstrapContext: (description: string) =>
    apiFetch<BootstrapContextResponse>('/sites/bootstrap-context', {
      method: 'POST',
      body: JSON.stringify({ description }),
    }),
  regenerateContext: (id: string) =>
    apiFetch<RegenerateContextResponse>(
      `/sites/${encodeURIComponent(id)}/regenerate-context`,
      { method: 'POST' },
    ),
  brainstormSuggestions: (id: string, opts?: { refresh?: boolean }) => {
    const qs = opts?.refresh ? '?refresh=true' : '';
    return apiFetch<BrainstormSuggestionsResponse>(
      `/sites/${encodeURIComponent(id)}/brainstorm-suggestions${qs}`,
    );
  },
  generatePromptsPreview: (req: GeneratePromptsPreviewRequest) =>
    apiFetch<GeneratePromptsResponse>('/sites/generate-prompts-preview', {
      method: 'POST',
      body: JSON.stringify(req),
    }),
  generatePromptsForSite: (siteId: string, req: GeneratePromptsForSiteRequest) =>
    apiFetch<GeneratePromptsResponse>(
      `/sites/${encodeURIComponent(siteId)}/generate-prompts`,
      { method: 'POST', body: JSON.stringify(req) },
    ),
  // acceptGeneratedPrompts persists operator-approved AI bodies as new
  // site-scope versions. The handler returns:
  //   200 — every persona saved → { status: "ok",      saved: {...} }
  //   207 — some saved, some failed → { status: "partial", saved, errors }
  //   500 — every persona failed → { status: "failed", errors: {...} }
  //
  // apiFetch treats 207 as a success (Response.ok covers 200..299) so the
  // promise resolves with the partial-success body; callers must inspect
  // `status` / `errors` to surface per-persona toasts. The legacy
  // `activated` alias is preserved alongside `saved` for callers that have
  // not migrated yet.
  acceptGeneratedPrompts: async (
    siteId: string,
    prompts: Record<string, { body: string; generator_version_id: string }>,
    changeNote?: string,
  ): Promise<AcceptGeneratedPromptsResult> => {
    const resp = await apiFetch<AcceptGeneratedPromptsResponse>(
      `/sites/${encodeURIComponent(siteId)}/generate-prompts/accept`,
      {
        method: 'POST',
        body: JSON.stringify({ prompts, change_note: changeNote }),
      },
    );
    const saved = resp.saved ?? {};
    return {
      status: resp.status,
      saved,
      errors: resp.errors ?? {},
      activated: saved,
    };
  },
  generateAuthoritySourcesPreview: (
    req: GenerateAuthoritySourcesPreviewRequest,
  ) =>
    apiFetch<GenerateAuthoritySourcesPreviewResponse>(
      '/sites/generate-authority-sources-preview',
      { method: 'POST', body: JSON.stringify(req) },
    ),
  generateAuthoritySources: (siteId: string, req: { feedback?: string }) =>
    apiFetch<GenerateAuthoritySourcesResponse>(
      `/sites/${encodeURIComponent(siteId)}/generate-authority-sources`,
      { method: 'POST', body: JSON.stringify(req) },
    ),
  bulkCreateAuthoritySources: (
    siteId: string,
    sources: BulkAuthoritySourceInput[],
  ) =>
    apiFetch<BulkAuthoritySourcesResponse>(
      `/sites/${encodeURIComponent(siteId)}/authority-sources/bulk`,
      { method: 'POST', body: JSON.stringify({ sources }) },
    ),
};

// BrainstormSuggestionsResponse mirrors the envelope returned by
// GET /api/sites/:id/brainstorm-suggestions. The endpoint is graceful — it
// never returns 5xx for chip-side failures; instead it returns 200 with
// `chips` set to whatever cache exists (possibly empty) and a `warning`
// string describing the failure mode. The dashboard treats any warning as
// "render whatever chips are there and stay silent" so the brainstorm
// textarea stays usable.
export interface BrainstormSuggestionsResponse {
  chips: string[];
  generated_at?: string;
  cached?: boolean;
  warning?: 'no_template' | 'claude_failed' | 'parse_failed' | 'not_configured';
}

// ReIllustrateResponse mirrors the 202 envelope returned by
// POST /api/sites/:id/re-illustrate. The actual regeneration runs in a
// goroutine server-side; this response only confirms the kickoff.
export interface ReIllustrateResponse {
  started: boolean;
  site_id: string;
  article_count: number;
}
